import React, { useEffect, useRef, useState } from 'react';
import { Send, X, Loader2, Info, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MODAL_OVERLAY_Z40_CLASS } from '../ui/modalOverlay';
import JustificatifInput from '../JustificatifInput';
import { api } from '../../services/api';

// Deux destinations seulement. La dotation de paie n'est volontairement pas
// proposee ici : elle se demande depuis l'état des salaires, seul endroit qui
// connaisse le net à doter, le mois concerné et le calendrier de paie.
const USAGES_DOTATION = [
  {
    valeur: 'budget_depenses',
    objet: '',
    titre: 'Budget dépenses',
    description: 'Pour les dépenses fixes et variables.'
  },
  {
    valeur: 'principale',
    objet: 'avances_salaire',
    titre: 'Avances sur salaire',
    description: 'Pour approvisionner la caisse en vue de verser des avances.'
  }
];

const TYPES_EMPLOYE = [
  { valeur: 'FIXE', libelle: 'Salaire fixe' },
  { valeur: 'HORAIRE', libelle: 'Taux horaire' },
  { valeur: 'HYBRIDE', libelle: 'Hybride' }
];

const formatFcfa = (valeur) => `${Math.round(Number(valeur) || 0).toLocaleString('fr-FR')} FCFA`;

const ModalDemanderDotation = ({
  isOpen,
  onClose,
  demande,
  setDemande,
  isSending,
  onSubmit,
  moisScolaires = []
}) => {
  const montantRef = useRef(null);

  const [employes, setEmployes] = useState([]);
  const [capacites, setCapacites] = useState(null);
  const [loadingCapacites, setLoadingCapacites] = useState(false);

  const pourAvances = demande.usage_dotation === 'principale';

  // Fermeture au clavier, alignee sur le comportement des autres modals KLASYS.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isSending) onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSending, onClose]);

  // Le montant est la premiere saisie attendue : on y place le focus a l'ouverture.
  useEffect(() => {
    if (isOpen) montantRef.current?.focus();
  }, [isOpen]);

  // Liste des employes du type choisi, pour le mois choisi.
  useEffect(() => {
    let annule = false;
    if (!isOpen || !pourAvances || !demande.mois_scolaire_id) {
      setEmployes([]);
      return undefined;
    }

    const charger = async () => {
      try {
        let reponse;
        if ((demande.type_employe || 'FIXE') === 'HORAIRE') {
          reponse = await api.avancesSalaire.getEnseignantsTauxHoraire(demande.mois_scolaire_id);
        } else if ((demande.type_employe || 'FIXE') === 'HYBRIDE') {
          reponse = await api.avancesSalaire.getEmployesHybrides(demande.mois_scolaire_id);
        } else {
          reponse = await api.salary.getEmployees();
        }
        if (annule) return;
        setEmployes(reponse?.success ? (reponse.data || []) : []);
      } catch (error) {
        console.error('Erreur chargement employes:', error);
        if (!annule) setEmployes([]);
      }
    };

    charger();
    return () => { annule = true; };
  }, [isOpen, pourAvances, demande.type_employe, demande.mois_scolaire_id]);

  // Capacite restante des employes selectionnes : c'est elle qui borne le montant.
  useEffect(() => {
    let annule = false;
    const ids = demande.employes_avances || [];

    if (!pourAvances || !demande.mois_scolaire_id || ids.length === 0) {
      setCapacites(null);
      return undefined;
    }

    const charger = async () => {
      setLoadingCapacites(true);
      try {
        const reponse = await api.avancesSalaire.getCapacite({
          mois_scolaire_id: demande.mois_scolaire_id,
          type_employe: demande.type_employe || 'FIXE',
          employe_ids: ids
        });
        if (!annule) setCapacites(reponse?.success ? reponse.data : null);
      } catch (error) {
        console.error('Erreur capacite avances:', error);
        if (!annule) setCapacites(null);
      } finally {
        if (!annule) setLoadingCapacites(false);
      }
    };

    charger();
    return () => { annule = true; };
  }, [pourAvances, demande.mois_scolaire_id, demande.employes_avances, demande.type_employe]);

  if (!isOpen) return null;

  const majDemande = (champs) => setDemande({ ...demande, ...champs });

  const idsSelectionnes = demande.employes_avances || [];
  const totalDisponible = capacites?.total_disponible || 0;
  const montantSaisi = Number(demande.montant) || 0;
  const depassePlafond = pourAvances && capacites && montantSaisi > totalDisponible;

  const basculerEmploye = (employeId) => {
    const id = Number(employeId);
    majDemande({
      employes_avances: idsSelectionnes.includes(id)
        ? idsSelectionnes.filter((valeur) => valeur !== id)
        : [...idsSelectionnes, id]
    });
  };

  const capacitePour = (employeId) =>
    (capacites?.lignes || []).find((ligne) => Number(ligne.employe_id) === Number(employeId));

  return (
    <div
      className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSending) onClose();
      }}
    >
      {/* Colonne flex : l'en-tete et le pied restent fixes, seul le corps defile. */}
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">

        {/* ================= EN-TETE ================= */}
        <div className="flex flex-shrink-0 items-start justify-between gap-4 bg-[#246990] px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
              Caisse centrale
            </p>
            <h3 className="flex items-center gap-3 text-xl font-semibold text-white">
              <Send size={22} />
              Demander une dotation
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            className="rounded-lg p-2 text-white transition-colors hover:bg-white/20 disabled:opacity-50"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>
        </div>

        {/* ================= CORPS DEFILANT ================= */}
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">

          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <Info size={18} className="mt-0.5 flex-shrink-0 text-blue-600" />
            <p className="text-sm text-blue-900">
              La demande est envoyée à la caisse centrale. Une validation crédite votre caisse
              selon l'usage choisi.
            </p>
          </div>

          {/* ================= 1. USAGE ================= */}
          <section>
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Usage de la dotation
            </h4>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {USAGES_DOTATION.map((usage) => {
                const actif = demande.usage_dotation === usage.valeur;
                return (
                  <button
                    key={usage.valeur}
                    type="button"
                    onClick={() => majDemande({
                      usage_dotation: usage.valeur,
                      objet_dotation: usage.objet,
                      employes_avances: []
                    })}
                    className={`rounded-lg border-2 p-3 text-left transition-colors ${
                      actif
                        ? 'border-[#246990] bg-[#f4f9fc]'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <p className={`font-semibold ${actif ? 'text-[#246990]' : 'text-gray-900'}`}>
                      {usage.titre}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-500">{usage.description}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ================= 1 bis. EMPLOYES CONCERNES (avances) ================= */}
          {pourAvances && (
            <section className="space-y-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Employés concernés
              </h4>

              <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                Cette dotation approvisionne votre caisse. Les avances elles-mêmes se créent
                ensuite dans le module Avances sur salaire, qui refait ses propres contrôles.
                Le montant demandé ne peut pas dépasser ce que les employés sélectionnés peuvent
                encore recevoir.
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="mois-dotation" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Mois concerné <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="mois-dotation"
                    value={demande.mois_scolaire_id || ''}
                    onChange={(e) => majDemande({ mois_scolaire_id: e.target.value, employes_avances: [] })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                  >
                    <option value="">Sélectionnez un mois</option>
                    {moisScolaires.map((mois) => (
                      <option key={mois.id} value={mois.id}>
                        {mois.nom} {mois.annee_scolaire || ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="mb-1.5 block text-sm font-medium text-gray-700">Type d'employé</span>
                  <div className="flex gap-1 rounded-lg border border-gray-300 p-1">
                    {TYPES_EMPLOYE.map((type) => (
                      <button
                        key={type.valeur}
                        type="button"
                        onClick={() => { majDemande({ type_employe: type.valeur, employes_avances: [] }); }}
                        className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
                          (demande.type_employe || 'FIXE') === type.valeur
                            ? 'bg-[#246990] text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {type.libelle}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {!demande.mois_scolaire_id ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  Sélectionnez un mois pour voir les employés et leur capacité d'avance.
                </p>
              ) : employes.length === 0 ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  Aucun employé de ce type pour le mois sélectionné.
                </p>
              ) : (
                <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-gray-200 p-2">
                  {employes.map((employe) => {
                    const id = Number(employe.id);
                    const coche = idsSelectionnes.includes(id);
                    const capacite = capacitePour(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => basculerEmploye(id)}
                        className={`flex w-full items-center justify-between gap-3 rounded border px-3 py-2 text-left transition-colors ${
                          coche ? 'border-[#246990] bg-[#f4f9fc]' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                          {employe.prenom} {employe.nom}
                        </span>
                        {coche && capacite && (
                          <span className="flex-shrink-0 text-xs text-gray-500">
                            {capacite.avances_existantes > 0
                              ? `déjà ${formatFcfa(capacite.avances_existantes)} · `
                              : ''}
                            <strong className={capacite.disponible > 0 ? 'text-emerald-700' : 'text-orange-700'}>
                              {capacite.disponible > 0 ? `${formatFcfa(capacite.disponible)} dispo.` : 'plafond atteint'}
                            </strong>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {idsSelectionnes.length > 0 && (
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-600">
                    Capacité totale ({idsSelectionnes.length} employé{idsSelectionnes.length > 1 ? 's' : ''})
                    {capacites ? ` · plafond ${capacites.pourcentage_max_avance_salaire}% du salaire` : ''}
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {loadingCapacites ? '...' : formatFcfa(totalDisponible)}
                  </span>
                </div>
              )}
            </section>
          )}

          {/* ================= 2. MONTANT ET MOTIF ================= */}
          <section className="space-y-3">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Détail de la demande
            </h4>

            <div>
              <label
                htmlFor="montant-dotation"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                Montant demandé <span className="text-red-500">*</span>
              </label>
              <div className="flex overflow-hidden rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-[#246990]">
                <Input
                  id="montant-dotation"
                  ref={montantRef}
                  type="number"
                  min="0"
                  step="1000"
                  inputMode="numeric"
                  placeholder="0"
                  value={demande.montant}
                  onChange={(e) => majDemande({ montant: e.target.value })}
                  className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <span className="inline-flex flex-shrink-0 items-center border-l border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-600">
                  FCFA
                </span>
              </div>

              {depassePlafond && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs text-orange-700">
                  <AlertCircle size={14} className="mt-px flex-shrink-0" />
                  Dépasse la capacité des employés sélectionnés ({formatFcfa(totalDisponible)}).
                  Réduisez le montant ou ajoutez des employés.
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="motif-dotation"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                Motif de la demande <span className="text-red-500">*</span>
              </label>
              <textarea
                id="motif-dotation"
                rows={3}
                placeholder="Expliquez le besoin de dotation..."
                value={demande.motif}
                onChange={(e) => majDemande({ motif: e.target.value })}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
              />
            </div>
          </section>

          {/* ================= 3. JUSTIFICATIF ================= */}
          <section>
            <JustificatifInput
              required
              file={demande.justificatif_fichier}
              onFileChange={(file) => majDemande({ justificatif_fichier: file })}
              title="Justificatif de dotation"
              description="Document, facture pro forma, liste de salaires ou photo justificative. Obligatoire."
              accentColor="#246990"
            />
          </section>
        </div>

        {/* ================= ACTIONS (pied fixe) ================= */}
        <div className="flex flex-shrink-0 flex-col gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isSending}
          >
            Annuler
          </Button>

          <Button
            size="sm"
            className="flex-1 bg-[#246990] text-white hover:bg-[#1d5778]"
            onClick={onSubmit}
            disabled={isSending || depassePlafond}
          >
            {isSending ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Envoyer la demande
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalDemanderDotation;
